var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useState, useRef, } from 'react';
import classNames from 'classnames';
import SelectSingleOption from './SelectSingleOption';
var SelectSingle = function (props) {
    var className = props.className, defaultValue = props.defaultValue, defaultLabel = props.defaultLabel, disabled = props.disabled, hidden = props.hidden, id = props.id, name = props.name, onChange = props.onChange, options = props.options, reset = props.reset, roleDescription = props.roleDescription, overflow = props.overflow, multilineOptions = props.multilineOptions, isSearchable = props.isSearchable, dataTestValue = props.dataTestValue;
    var defaultSelectedObject = {
        text: defaultLabel || 'Select an option',
        value: null,
    };
    var _a = useState(options.find(function (option) { return option.value === defaultValue; }) || defaultSelectedObject), selectedOption = _a[0], setSelectedOption = _a[1];
    var _b = useState(0), optionCursor = _b[0], setOptionCursor = _b[1];
    var _c = useState(false), isOpen = _c[0], toggleOpen = _c[1];
    var _d = useState(false), openedByKeypress = _d[0], setKeypressOpened = _d[1];
    var _e = useState(false), clickedOnChild = _e[0], setClickedOnChild = _e[1];
    var _f = useState(''), searchValue = _f[0], setSearchValue = _f[1];
    var _g = useState(options), filteredOptions = _g[0], setFilteredOptions = _g[1];
    var _h = useState(false), searchFocus = _h[0], setSearchFocus = _h[1];
    var _j = useState(false), optionsFocus = _j[0], setOptionsFocus = _j[1];
    var searchInputRef = useRef(null);
    var optionRefs = [];
    // plus 1 to account for default select object
    for (var idx = 0; idx < options.length + 1; idx++) {
        optionRefs.push(React.createRef());
    }
    useEffect(function () {
        if (reset) {
            // Resets select
            setSelectedOption(options.find(function (option) { return option.value === defaultValue; }) || defaultSelectedObject);
            setOptionCursor(0);
            toggleOpen(false);
            setKeypressOpened(false);
            setClickedOnChild(false);
            setSearchValue('');
        }
    }, [reset, options, defaultValue, defaultSelectedObject]);
    useEffect(function () {
        var _a, _b;
        if (isOpen && openedByKeypress) {
            (_a = optionRefs[optionCursor]) === null || _a === void 0 ? void 0 : _a.current.focus();
        }
        if (isOpen && !openedByKeypress) {
            (_b = searchInputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (isSearchable) {
            setSearchValue('');
        }
    }, [isOpen, openedByKeypress]);
    // update filter when options/search value prop changes\
    useEffect(function () {
        if (isSearchable && searchValue) {
            var filterRegEx_1 = new RegExp(searchValue, 'i');
            return setFilteredOptions(__spreadArray([defaultSelectedObject], options.filter(function (_a) {
                var text = _a.text;
                return (filterRegEx_1.test(text));
            }), true));
        }
        return setFilteredOptions(__spreadArray([defaultSelectedObject], options, true));
    }, [options, searchValue]);
    // open close the options based on focus state
    useEffect(function () {
        if (!searchFocus && !optionsFocus && !openedByKeypress && !clickedOnChild) {
            toggleOpen(false);
        }
        if (clickedOnChild) {
            setClickedOnChild(false);
        }
    }, [searchFocus, optionsFocus, openedByKeypress]);
    var _expandHandler = function (e) {
        if (e.type === 'click') {
            setKeypressOpened(false);
            toggleOpen(!isOpen);
        }
        else if ((e.key === ' ' && !searchFocus) || e.key === 'Enter') {
            setKeypressOpened(true);
            toggleOpen(!isOpen);
        }
    };
    var _optionHandler = function (e, option, idx) {
        if (!hidden && e.key === 'ArrowUp' && e.target.previousSibling) {
            // Moves up one option in open select menu
            e.target.previousSibling.focus();
            return;
        }
        if (!hidden && e.key === 'ArrowDown' && e.target.nextSibling) {
            // Moves down one option in open select menu
            e.target.nextSibling.focus();
            return;
        }
        if (!disabled && !hidden) {
            if (e.type === 'click' || e.key === ' ' || e.key === 'Enter') {
                if (option.disabled) {
                    // Stops select from collapsing when choosing disabled option
                    e.stopPropagation();
                    return;
                }
                setSelectedOption(option);
                setOptionCursor(idx);
                if (onChange && typeof onChange === 'function') {
                    // Passes event to external change handler if one exists
                    var processedEvent = __assign({}, e);
                    processedEvent.target.name = name;
                    processedEvent.target.value = option.value;
                    onChange(processedEvent);
                }
            }
        }
    };
    var _selectName = classNames('clyde-select clyde-select-single', className, {
        'clyde-select--disabled': disabled,
        'clyde-select--hidden': hidden,
    });
    var _optionsListName = classNames('clyde-select-single__options-list', {
        'clyde-select-single__options-list--open': isOpen,
        'clyde-select-single__options-list--open--overflow': isOpen && overflow,
    });
    return (React.createElement("div", { "aria-disabled": disabled, "aria-hidden": hidden, "aria-roledescription": roleDescription, className: _selectName, id: id, "data-test": dataTestValue, onClick: _expandHandler, onKeyUp: _expandHandler, onBlur: function () { return setOptionsFocus(false); }, onFocus: function () { return setOptionsFocus(true); }, role: 'listbox', tabIndex: 0 },
        isOpen && isSearchable ? (React.createElement("input", { placeholder: "Search", ref: searchInputRef, className: 'clyde-select__input', value: searchValue, onBlur: function () { return setSearchFocus(false); }, onFocus: function () { return setSearchFocus(true); }, onChange: function (e) { return setSearchValue(e.currentTarget.value); } })) : (React.createElement("span", { className: 'clyde-select-single__unselectable' }, selectedOption.text)),
        React.createElement("div", { className: _optionsListName, "data-test": 'selectOptionsArea' }, filteredOptions.map(function (option, idx) { return (React.createElement(SelectSingleOption, { key: "so-".concat(idx), option: option, multilineOptions: multilineOptions, ref: optionRefs[idx], selected: selectedOption.text === option.text && selectedOption.value === option.value, _optionHandler: function (e, handlerOption) { _optionHandler(e, handlerOption, idx); }, _clickedOnChild: setClickedOnChild })); })),
        React.createElement("div", { className: "clyde-select-arrow--".concat(isOpen ? 'open' : 'closed') })));
};
export default SelectSingle;
